import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Modal,
  TextInput,
  SegmentedControl,
  ActionIcon,
  Text,
  Loader,
  Center,
  Pagination,
  Badge,
  Button,
  Switch,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconFilter,
  IconPlus,
  IconX,
  IconCheck,
} from "@tabler/icons-react";
import { useQuery, useQueries } from "@tanstack/react-query";
import {
  mvpApiListStorePlanograms,
  type PlanogramDetailSchema,
  type PlanogramProductSchema,
  type PagedStorePlanogramSchema,
} from "@labeling-tools/api/atlas";
import { mvpApiGetPlanogramOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";

type SearchMode = "planograms" | "products";

const PAGE_SIZE = 50;

export interface PlanogramSlotEdit {
  aisleIndex: number; // -1 for newly added slots
  planogramId: string;
  label: string;
}

export interface PlanogramSearchError {
  code: string;
  message: string;
}

interface PlanogramSearchModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (slots: PlanogramSlotEdit[]) => void;
  onError?: (error: PlanogramSearchError | null) => void;
  retailerId?: string | null;
  storeId?: string | null;
  currentSlots: { aisleIndex: number; planogramId: string; label: string }[];
  preselectedSlot?: number | null;
  readOnly?: boolean;
  showAllToggle?: boolean;
}

export const PlanogramSearchModal = ({
  opened,
  onClose,
  onSubmit,
  onError,
  retailerId,
  storeId,
  currentSlots,
  preselectedSlot,
  readOnly,
  showAllToggle,
}: PlanogramSearchModalProps) => {
  const [searchMode, setSearchMode] = useState<SearchMode>("planograms");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchText, 300);
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  // Editable local copy of slots
  const [editableSlots, setEditableSlots] = useState<PlanogramSlotEdit[]>([]);
  const [activeSlotIndex, setActiveSlotIndex] = useState<number | null>(null);

  // Initialize editable slots when modal opens
  useEffect(() => {
    if (opened) {
      const slots = currentSlots.map((s) => ({ ...s }));
      setEditableSlots(slots);
      setActiveSlotIndex(preselectedSlot ?? (slots.length > 0 ? 0 : null));
      setSearchText("");
      setPage(1);
      setShowAll(false);
    }
  }, [opened, currentSlots, preselectedSlot]);

  // Reset pagination when store/retailer context changes while modal is open
  useEffect(() => {
    if (opened) {
      setPage(1);
    }
  }, [retailerId, storeId, opened]);

  // Report context errors to parent
  useEffect(() => {
    if (!opened) {
      return;
    }
    if (!retailerId || !storeId) {
      const message =
        !retailerId && !storeId
          ? "Missing retailer and store context for this task."
          : !retailerId
            ? "Missing retailer context for this task."
            : "Missing store context for this task.";
      onError?.({ code: "PLANO-001", message });
    } else {
      onError?.(null);
    }
  }, [opened, retailerId, storeId, onError]);

  const handleSearchChange = useCallback((val: string) => {
    setSearchText(val);
    setPage(1);
  }, []);

  const handleModeChange = useCallback((val: string) => {
    setSearchMode(val as SearchMode);
    setPage(1);
  }, []);

  // --- Core data: store-planograms (active only, or all for admin) ---
  const activeFilter = showAll ? undefined : true;

  const {
    data: storePlanogramData,
    isLoading: storePlanogramLoading,
    error: storePlanogramError,
  } = useQuery({
    queryKey: ["store-planograms", storeId, activeFilter],
    queryFn: async () => {
      const response = await mvpApiListStorePlanograms({
        query: {
          store_id: storeId!,
          ...(activeFilter !== undefined ? { active: activeFilter } : {}),
          page_size: 200,
        },
      });
      if (response.error) throw new Error(`${response.error}`);
      return response.data as PagedStorePlanogramSchema;
    },
    enabled: opened && !!storeId,
  });

  // Map planogram ID → active status (for badges)
  const activeStatusMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const sp of storePlanogramData?.store_planograms ?? []) {
      const pid = sp.planogram?.id;
      if (pid) map.set(pid, sp.active ?? false);
    }
    return map;
  }, [storePlanogramData]);

  // Extract planogram IDs from store-planogram results
  const storePlanogramIds = useMemo(
    () =>
      (storePlanogramData?.store_planograms ?? [])
        .map((sp) => sp.planogram?.id)
        .filter((id): id is string => !!id),
    [storePlanogramData],
  );

  // Batch fetch details for all planograms (images + products)
  const detailQueries = useQueries({
    queries: storePlanogramIds.map((id) => ({
      ...mvpApiGetPlanogramOptions({ path: { planogram_id: id } }),
      enabled: !!id,
      staleTime: 30_000,
    })),
  });

  const allPlanogramDetails = useMemo(
    () =>
      detailQueries
        .map((q) => q.data as PlanogramDetailSchema | undefined)
        .filter((d): d is PlanogramDetailSchema => !!d),
    [detailQueries],
  );

  const detailsLoading =
    storePlanogramLoading || detailQueries.some((q) => q.isLoading);

  // --- Planograms mode: paginated + fuzzy filtered ---
  const planogramPageResults = useMemo(() => {
    let filtered = allPlanogramDetails;
    if (debouncedSearch && searchMode === "planograms") {
      filtered = filtered.filter((p) =>
        fuzzyMatch(p.description ?? "", debouncedSearch),
      );
    }
    return filtered;
  }, [allPlanogramDetails, debouncedSearch, searchMode]);

  const totalPages = Math.max(
    1,
    Math.ceil(planogramPageResults.length / PAGE_SIZE),
  );
  const paginatedPlanograms = useMemo(
    () => planogramPageResults.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [planogramPageResults, page],
  );

  // --- Products mode: client-side product search ---
  const productSearchResults = useMemo(() => {
    if (
      searchMode !== "products" ||
      !debouncedSearch ||
      !allPlanogramDetails.length
    ) {
      return [];
    }
    return allPlanogramDetails
      .map((planogram) => {
        const matchingProducts = planogram.products.filter(
          (p) =>
            fuzzyMatch(p.description, debouncedSearch) ||
            fuzzyMatch(p.identifier ?? "", debouncedSearch),
        );
        if (matchingProducts.length === 0) return null;
        return { planogram, matchingProducts };
      })
      .filter(
        (
          r,
        ): r is {
          planogram: PlanogramDetailSchema;
          matchingProducts: PlanogramProductSchema[];
        } => r !== null,
      );
  }, [searchMode, debouncedSearch, allPlanogramDetails]);

  const isLoading = detailsLoading;

  // Set of planogram IDs assigned to any slot (for badges in grid)
  const assignedPlanogramIds = useMemo(() => {
    const set = new Set<string>();
    for (const slot of editableSlots) {
      if (slot.planogramId) set.add(slot.planogramId);
    }
    return set;
  }, [editableSlots]);

  // Assign a planogram to the active slot (block inactive planograms)
  const handleCardClick = useCallback(
    (planogramId: string, planogramLabel: string) => {
      if (activeSlotIndex === null) return;
      if (showAll && activeStatusMap.get(planogramId) === false) return;
      if (readOnly) {
        const updated = editableSlots.map((slot, i) =>
          i === activeSlotIndex
            ? { ...slot, planogramId, label: planogramLabel }
            : slot,
        );
        onSubmit(updated);
        onClose();
        return;
      }
      setEditableSlots((prev) => {
        const next = [...prev];
        if (next[activeSlotIndex]) {
          next[activeSlotIndex] = {
            ...next[activeSlotIndex],
            planogramId,
            label: planogramLabel,
          };
        }
        return next;
      });
    },
    [
      activeSlotIndex,
      readOnly,
      editableSlots,
      onSubmit,
      onClose,
      showAll,
      activeStatusMap,
    ],
  );

  // Add a new empty slot
  const handleAddSlot = useCallback(() => {
    setEditableSlots((prev) => {
      const newSlot: PlanogramSlotEdit = {
        aisleIndex: -1,
        planogramId: "",
        label: `Planogram ${prev.length + 1}`,
      };
      return [...prev, newSlot];
    });
    setActiveSlotIndex(editableSlots.length);
  }, [editableSlots.length]);

  // Remove a user-added slot
  const handleRemoveSlot = useCallback(
    (index: number) => {
      setEditableSlots((prev) => prev.filter((_, i) => i !== index));
      if (activeSlotIndex === index) {
        setActiveSlotIndex(index > 0 ? index - 1 : 0);
      } else if (activeSlotIndex !== null && activeSlotIndex > index) {
        setActiveSlotIndex(activeSlotIndex - 1);
      }
    },
    [activeSlotIndex],
  );

  const handleSubmit = useCallback(() => {
    onSubmit(editableSlots);
    onClose();
  }, [editableSlots, onSubmit, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Select Planogram"
      size="80%"
      centered
      zIndex={10000}
      withinPortal
      portalProps={{ target: "body" }}
      lockScroll={false}
      styles={{
        overlay: { position: "fixed", inset: 0 },
        inner: { position: "fixed", inset: 0 },
        body: { padding: 0 },
      }}
      classNames={{ content: "planogram-search-modal" }}
    >
      <div className="flex flex-col" style={{ height: "75vh" }}>
        {/* Two-column layout */}
        <div className="flex flex-1 min-h-0">
          {/* Left sidebar — slot list */}
          <div className="w-[240px] flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">
            <div className="px-3 py-2 border-b border-gray-200">
              <Text fw={600} size="sm" c="dimmed" tt="uppercase">
                Planogram Slots
              </Text>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {editableSlots.map((slot, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer mb-1 transition-colors ${
                    i === activeSlotIndex
                      ? "bg-blue-100 border border-blue-300"
                      : "hover:bg-gray-100 border border-transparent"
                  }`}
                  onClick={() => setActiveSlotIndex(i)}
                >
                  <div className="flex-1 min-w-0">
                    <Text size="xs" fw={600} lineClamp={1}>
                      Slot {i + 1}
                    </Text>
                    <Text
                      size="xs"
                      c={slot.planogramId ? "dimmed" : "red"}
                      lineClamp={1}
                    >
                      {slot.planogramId ? slot.label : "Not assigned"}
                    </Text>
                  </div>
                  {!readOnly && slot.aisleIndex === -1 && (
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSlot(i);
                      }}
                      title="Remove slot"
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  )}
                </div>
              ))}
            </div>
            {!readOnly && editableSlots.length < 2 && (
              <div className="p-2 border-t border-gray-200">
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  fullWidth
                  onClick={handleAddSlot}
                >
                  Add Planogram
                </Button>
              </div>
            )}
          </div>

          {/* Right side — search and results */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden px-4 pt-3">
            {/* Mode toggle + search bar + admin toggle */}
            <div className="flex items-center gap-2 pb-3 flex-shrink-0">
              <SegmentedControl
                value={searchMode}
                onChange={handleModeChange}
                data={[
                  { label: "Planograms", value: "planograms" },
                  { label: "Products", value: "products" },
                ]}
                size="xs"
              />
              <TextInput
                placeholder={
                  searchMode === "planograms"
                    ? "Search planograms by name..."
                    : "Search by product name or SKU..."
                }
                leftSection={<IconSearch size={16} />}
                value={searchText}
                onChange={(e) => handleSearchChange(e.currentTarget.value)}
                className="flex-1"
                size="sm"
              />
              {showAllToggle && (
                <div className="flex-shrink-0">
                  <Switch
                    label="Show all"
                    size="sm"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.currentTarget.checked)}
                  />
                </div>
              )}
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                title="Filters"
              >
                <IconFilter size={16} />
              </ActionIcon>
            </div>

            {activeSlotIndex === null && (
              <Center className="flex-1">
                <Text c="dimmed" size="sm">
                  Select a slot on the left to assign a planogram
                </Text>
              </Center>
            )}

            {activeSlotIndex !== null && (!retailerId || !storeId) && (
              <Center className="flex-1">
                <div className="text-center">
                  <Text c="red" size="sm" fw={600}>
                    Unable to load planograms
                  </Text>
                  <Text c="dimmed" size="xs" mt={4}>
                    {!retailerId && !storeId
                      ? "Missing retailer and store context for this task."
                      : !retailerId
                        ? "Missing retailer context for this task."
                        : "Missing store context for this task."}
                  </Text>
                  <Text c="dimmed" size="xs" mt={2}>
                    Error code: PLANO-001
                  </Text>
                </div>
              </Center>
            )}

            {activeSlotIndex !== null && !!retailerId && !!storeId && (
              <>
                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-scroll min-h-0">
                  {isLoading ? (
                    <Center className="h-full">
                      <Loader />
                    </Center>
                  ) : storePlanogramError ? (
                    <Center className="h-full">
                      <Text c="red" size="sm">
                        Failed to load planograms. Check console for details.
                      </Text>
                    </Center>
                  ) : searchMode === "products" ? (
                    <ProductSearchResults
                      results={productSearchResults}
                      activeSlotPlanogramId={
                        editableSlots[activeSlotIndex]?.planogramId ?? null
                      }
                      assignedPlanogramIds={assignedPlanogramIds}
                      activeStatusMap={showAll ? activeStatusMap : undefined}
                      onCardClick={handleCardClick}
                      searchText={debouncedSearch}
                    />
                  ) : (
                    <PlanogramResults
                      planograms={paginatedPlanograms}
                      activeSlotPlanogramId={
                        editableSlots[activeSlotIndex]?.planogramId ?? null
                      }
                      assignedPlanogramIds={assignedPlanogramIds}
                      activeStatusMap={showAll ? activeStatusMap : undefined}
                      onCardClick={handleCardClick}
                    />
                  )}
                </div>

                {/* Pagination — planograms mode only */}
                {searchMode === "planograms" && totalPages > 1 && (
                  <Center className="pt-3 flex-shrink-0">
                    <Pagination
                      value={page}
                      onChange={setPage}
                      total={totalPages}
                      size="sm"
                    />
                  </Center>
                )}
              </>
            )}
          </div>
        </div>

        {/* Bottom bar — Cancel + Submit */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 flex-shrink-0">
          <Button variant="default" onClick={handleCancel}>
            Cancel
          </Button>
          {!readOnly && <Button onClick={handleSubmit}>Submit</Button>}
        </div>
      </div>
    </Modal>
  );
};

// --- Shared utilities ---

/** Simple fuzzy match: all search words must appear in the target (case-insensitive). */
const fuzzyMatch = (target: string, search: string): boolean => {
  if (!search) return true;
  const lowerTarget = target.toLowerCase();
  return search
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => lowerTarget.includes(word));
};

/** Active/Inactive badge shown when admin has "Show all" toggled on */
const ActiveBadge = ({ active }: { active: boolean }) => (
  <Badge size="xs" color={active ? "green" : "red"} variant="light">
    {active ? "Active" : "Inactive"}
  </Badge>
);

// --- Planograms mode results ---

const PlanogramResults = ({
  planograms,
  activeSlotPlanogramId,
  assignedPlanogramIds,
  activeStatusMap,
  onCardClick,
}: {
  planograms: PlanogramDetailSchema[];
  activeSlotPlanogramId: string | null;
  assignedPlanogramIds: Set<string>;
  activeStatusMap?: Map<string, boolean>;
  onCardClick: (planogramId: string, label: string) => void;
}) => {
  if (planograms.length === 0) {
    return (
      <Center className="h-[400px]">
        <Text c="dimmed" size="sm">
          No planograms found
        </Text>
      </Center>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {planograms.map((p) => (
        <PlanogramCard
          key={p.id}
          planogram={p}
          isActiveSlotSelection={activeSlotPlanogramId === p.id}
          isAssigned={assignedPlanogramIds.has(p.id ?? "")}
          activeStatus={activeStatusMap?.get(p.id ?? "")}
          onCardClick={onCardClick}
        />
      ))}
    </div>
  );
};

const PlanogramCard = ({
  planogram,
  isActiveSlotSelection,
  isAssigned,
  activeStatus,
  onCardClick,
}: {
  planogram: PlanogramDetailSchema;
  isActiveSlotSelection: boolean;
  isAssigned: boolean;
  activeStatus?: boolean;
  onCardClick: (planogramId: string, label: string) => void;
}) => {
  const planogramId = planogram.id ?? "";
  const label = planogram.description ?? "Untitled";
  const inactive = activeStatus === false;

  return (
    <div
      className={`border-2 rounded-lg overflow-hidden transition-all ${
        inactive
          ? "opacity-50 cursor-not-allowed border-transparent"
          : isActiveSlotSelection
            ? "border-blue-500 shadow-md cursor-pointer"
            : "border-transparent hover:border-gray-300 hover:shadow-md cursor-pointer"
      }`}
      onClick={() => onCardClick(planogramId, label)}
    >
      <div className="bg-gray-100 h-[120px] flex items-center justify-center overflow-hidden relative">
        {planogram.image_url ? (
          <img
            src={planogram.image_url}
            alt={label}
            className="w-full h-full object-cover"
          />
        ) : (
          <Text c="dimmed" size="xs">
            No image
          </Text>
        )}
        {isActiveSlotSelection && (
          <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
            <IconCheck size={14} />
          </div>
        )}
        {isAssigned && !isActiveSlotSelection && (
          <div className="absolute top-2 right-2">
            <Badge size="xs" color="gray">
              Assigned
            </Badge>
          </div>
        )}
      </div>
      <div className="p-2">
        <div className="flex items-center gap-2">
          <Text size="sm" fw={600} lineClamp={1} className="flex-1 min-w-0">
            {label}
          </Text>
          {activeStatus !== undefined && <ActiveBadge active={activeStatus} />}
        </div>
        <Text size="xs" c="dimmed" mt={2}>
          {planogram.retailer?.name}
        </Text>
      </div>
    </div>
  );
};

// --- Products mode results ---

interface ProductSearchResult {
  planogram: PlanogramDetailSchema;
  matchingProducts: PlanogramProductSchema[];
}

const ProductSearchResults = ({
  results,
  activeSlotPlanogramId,
  assignedPlanogramIds,
  activeStatusMap,
  onCardClick,
  searchText,
}: {
  results: ProductSearchResult[];
  activeSlotPlanogramId: string | null;
  assignedPlanogramIds: Set<string>;
  activeStatusMap?: Map<string, boolean>;
  onCardClick: (planogramId: string, label: string) => void;
  searchText: string;
}) => {
  if (!searchText) {
    return (
      <Center className="h-[400px]">
        <Text c="dimmed" size="sm">
          Type a product name or SKU to search across store planograms
        </Text>
      </Center>
    );
  }

  if (results.length === 0) {
    return (
      <Center className="h-[400px]">
        <Text c="dimmed" size="sm">
          No products found matching &quot;{searchText}&quot;
        </Text>
      </Center>
    );
  }

  return (
    <div className="space-y-3">
      {results.map(({ planogram, matchingProducts }) => (
        <ProductSearchPlanogramCard
          key={planogram.id}
          planogram={planogram}
          matchingProducts={matchingProducts}
          isActiveSlotSelection={activeSlotPlanogramId === planogram.id}
          isAssigned={assignedPlanogramIds.has(planogram.id ?? "")}
          activeStatus={activeStatusMap?.get(planogram.id ?? "")}
          onCardClick={onCardClick}
        />
      ))}
    </div>
  );
};

const ProductSearchPlanogramCard = ({
  planogram,
  matchingProducts,
  isActiveSlotSelection,
  isAssigned,
  activeStatus,
  onCardClick,
}: {
  planogram: PlanogramDetailSchema;
  matchingProducts: PlanogramProductSchema[];
  isActiveSlotSelection: boolean;
  isAssigned: boolean;
  activeStatus?: boolean;
  onCardClick: (planogramId: string, label: string) => void;
}) => {
  const planogramId = planogram.id ?? "";
  const label = planogram.description ?? "Untitled";
  const inactive = activeStatus === false;

  return (
    <div
      className={`border-2 rounded-lg overflow-hidden transition-all ${
        inactive
          ? "opacity-50 cursor-not-allowed border-transparent"
          : isActiveSlotSelection
            ? "border-blue-500 shadow-md cursor-pointer"
            : "border-transparent hover:border-gray-300 hover:shadow-md cursor-pointer"
      }`}
      onClick={() => onCardClick(planogramId, label)}
    >
      {/* Planogram header */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 border-b border-gray-200">
        {planogram.image_url ? (
          <img
            src={planogram.image_url}
            alt={label}
            className="w-16 h-10 rounded object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-10 rounded bg-gray-200 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Text size="sm" fw={600} lineClamp={1} className="min-w-0">
              {label}
            </Text>
            {activeStatus !== undefined && (
              <ActiveBadge active={activeStatus} />
            )}
          </div>
          <Text size="xs" c="dimmed">
            {matchingProducts.length} matching product(s)
          </Text>
        </div>
        {isActiveSlotSelection && (
          <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
            <IconCheck size={14} />
          </div>
        )}
        {isAssigned && !isActiveSlotSelection && (
          <Badge size="xs" color="gray" className="flex-shrink-0">
            Assigned
          </Badge>
        )}
      </div>

      {/* Matching products */}
      <div className="p-3 space-y-1.5">
        {matchingProducts.slice(0, 5).map((product) => (
          <div key={product.id} className="flex items-center gap-2 text-xs">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt=""
                className="w-6 h-6 rounded object-contain bg-white border border-gray-100 flex-shrink-0"
              />
            ) : (
              <div className="w-6 h-6 rounded bg-gray-200 flex-shrink-0" />
            )}
            <span className="truncate flex-1">{product.description}</span>
            {product.identifier && (
              <span className="text-gray-400 flex-shrink-0">
                {product.identifier}
              </span>
            )}
          </div>
        ))}
        {matchingProducts.length > 5 && (
          <Text size="xs" c="dimmed">
            +{matchingProducts.length - 5} more
          </Text>
        )}
      </div>
    </div>
  );
};
